/**
 * Compact Malay Learning Progress cards on learner home (Phase 2D.5).
 * Shows activity completion + formative strengths — never official marks,
 * never invents evidence when the backend has none.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useFeatureAccess } from '../../contexts/FeatureAccessContext';
import { useTutorTenantLabels } from '../../modules/tutor/hooks/useTutorTenantLabels';
import { useMalayFormativeHome } from '../../modules/student-dashboard/hooks/useMalayFormativeHome';
import {
  cardHasFormativeEvidence,
  continueRouteForActivity,
  narrativeWritingContinueRoute,
  narrativeWritingCtaKey,
  resolveActivityDisplayTitle,
  resolveCardStatusKey,
  resolveNarrativeDraftStatusKey,
  resolveNarrativePlotStatusKey,
  resolveStandardCardCtaKey,
} from '../../modules/tutor/malayFormativeHomeCards';
import { ROUTES } from '../../utils/constants';

function masteryLabel(level: string | undefined, t: (k: string) => string): string {
  switch (level) {
    case 'beginning':
      return t('dashboard.malayFormative.levels.beginning');
    case 'developing':
      return t('dashboard.malayFormative.levels.developing');
    case 'secure':
      return t('dashboard.malayFormative.levels.secure');
    case 'strong':
      return t('dashboard.malayFormative.levels.strong');
    case 'not_enough_evidence':
      return t('dashboard.malayFormative.levels.notEnoughEvidence');
    default:
      return t('dashboard.malayFormative.levels.notEnoughEvidence');
  }
}

function formatLastActivity(
  iso: string | undefined,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return t('dashboard.malayFormative.lastActivity', {
      date: d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    });
  } catch {
    return null;
  }
}

export function MalayLearningProgressSection() {
  const { t, i18n } = useTranslation();
  const { loading: featureLoading } = useFeatureAccess();
  const { loading: labelsLoading } = useTutorTenantLabels();
  const { enabled, cards, loading, error, reload } = useMalayFormativeHome(true);

  if (featureLoading || labelsLoading) return null;
  if (!enabled) return null;

  return (
    <section id="continue-learning" className="mb-6" aria-labelledby="malay-learning-progress-heading">
      <Card className="border-app-border bg-app-surface shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
          <CardTitle id="malay-learning-progress-heading" className="text-lg font-bold">
            {t('dashboard.malayFormative.title')}
          </CardTitle>
          <Link
            to={ROUTES.STUDENT_MALAY_LEARNING_PROGRESS}
            className="text-sm font-semibold text-app-primary hover:underline"
          >
            {t('dashboard.malayFormative.viewDetail')}
          </Link>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div
              className="flex items-center gap-2 py-8 text-app-text-muted"
              role="status"
              data-testid="malay-formative-loading"
            >
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              {t('dashboard.malayFormative.loading')}
            </div>
          ) : (
            <>
          {error ? (
            <div
              className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
              data-testid="malay-formative-error"
            >
              <p>{t(error)}</p>
              <button
                type="button"
                className="mt-3 inline-flex min-h-[40px] items-center rounded-full bg-amber-800/10 px-4 py-1.5 text-xs font-bold hover:bg-amber-800/15"
                onClick={reload}
              >
                {t('dashboard.malayFormative.retry')}
              </button>
            </div>
          ) : null}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {cards.map((card) => {
                const isNarrative =
                  card.activity_key === 'malay_narrative_learning_tutor';
                if (isNarrative) {
                  const plotKey = resolveNarrativePlotStatusKey(card.narrative_writing);
                  const draftKey = resolveNarrativeDraftStatusKey(card.narrative_writing);
                  const ctaKind = narrativeWritingCtaKey(card);
                  const ctaLabel =
                    ctaKind === 'completed'
                      ? t('dashboard.malayFormative.viewDetail')
                      : ctaKind === 'continueWriting'
                        ? t('dashboard.malayFormative.narrativeWriting.continueWriting')
                        : ctaKind === 'startWriting'
                          ? t('dashboard.malayFormative.narrativeWriting.startWriting')
                          : t('dashboard.malayFormative.start');
                  const continueTo =
                    ctaKind === 'completed'
                      ? ROUTES.STUDENT_MALAY_LEARNING_PROGRESS
                      : narrativeWritingContinueRoute(card);
                  const lastSavedIso = card.narrative_writing?.last_saved_at || undefined;
                  let lastSavedDate: string | null = null;
                  if (lastSavedIso) {
                    const d = new Date(lastSavedIso);
                    if (!Number.isNaN(d.getTime())) {
                      lastSavedDate = d.toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      });
                    }
                  }
                  const plotLabelKey =
                    plotKey === 'completed'
                      ? 'completed'
                      : plotKey === 'inProgress' || plotKey === 'started'
                        ? 'inProgress'
                        : 'notStarted';
                  const draftLabelKey =
                    draftKey === 'inProgress' || draftKey === 'completed'
                      ? 'inProgress'
                      : draftKey === 'started'
                        ? 'started'
                        : 'notStarted';
                  return (
                    <article
                      key={card.activity_key}
                      className="flex flex-col rounded-2xl border border-app-border bg-white/80 p-4 dark:bg-gray-900/40"
                      data-testid={`malay-formative-card-${card.activity_key}`}
                      data-has-evidence={cardHasFormativeEvidence(card) ? 'true' : 'false'}
                      data-narrative-dual="true"
                    >
                      <h3 className="text-sm font-bold text-app-text">
                        {resolveActivityDisplayTitle(card, i18n.language, t)}
                      </h3>
                      <div
                        className="mt-2 space-y-1 text-xs"
                        data-testid="malay-formative-narrative-dual"
                      >
                        <p>
                          <span className="font-semibold">
                            {t('dashboard.malayFormative.narrativeWriting.plotPlanning')}:
                          </span>{' '}
                          <span data-testid="narrative-plot-status">
                            {t(
                              `dashboard.malayFormative.narrativeWriting.status.${plotLabelKey}`,
                            )}
                          </span>
                        </p>
                        <p>
                          <span className="font-semibold">
                            {t('dashboard.malayFormative.narrativeWriting.storyDraft')}:
                          </span>{' '}
                          <span data-testid="narrative-draft-status">
                            {t(
                              `dashboard.malayFormative.narrativeWriting.status.${draftLabelKey}`,
                            )}
                          </span>
                        </p>
                        {lastSavedDate ? (
                          <p
                            className="text-app-text-muted"
                            data-testid="narrative-last-saved"
                          >
                            {t('dashboard.malayFormative.narrativeWriting.lastSaved', {
                              date: lastSavedDate,
                            })}
                          </p>
                        ) : null}
                      </div>
                      {!cardHasFormativeEvidence(card) &&
                      plotKey === 'notStarted' &&
                      draftKey === 'notStarted' ? (
                        <div
                          className="mt-2 space-y-1"
                          data-testid="malay-formative-empty-evidence"
                        >
                          <p className="text-xs font-medium text-app-text">
                            {t('dashboard.malayFormative.empty')}
                          </p>
                          <p className="text-xs text-app-text-muted">
                            {t('dashboard.malayFormative.emptyCardHint')}
                          </p>
                        </div>
                      ) : null}
                      <Link
                        to={continueTo}
                        className="mt-4 inline-flex items-center justify-center rounded-full bg-app-primary px-4 py-2 text-xs font-bold text-white hover:brightness-110"
                        data-testid="narrative-writing-cta"
                      >
                        {ctaLabel}
                      </Link>
                    </article>
                  );
                }

                if (card.activity_key === 'malay_jendela_seni_reading') {
                  const slice = card.reading_practice;
                  const attempted = Number(slice?.attempted_passages || 0);
                  const totalPassages = Number(slice?.total_passages || 8);
                  const average = slice?.average_best_score;
                  const continueTo = continueRouteForActivity(card.activity_key);
                  const ctaLabel =
                    attempted > 0
                      ? t('dashboard.malayFormative.resume')
                      : t('dashboard.malayFormative.start');
                  const progressRatio =
                    totalPassages > 0 ? Math.min(1, attempted / totalPassages) : 0;
                  return (
                    <article
                      key={card.activity_key}
                      className="flex flex-col rounded-2xl border border-app-border bg-white/80 p-4 dark:bg-gray-900/40"
                      data-testid={`malay-formative-card-${card.activity_key}`}
                    >
                      <h3 className="text-sm font-bold text-app-text">
                        {resolveActivityDisplayTitle(card, i18n.language, t)}
                      </h3>
                      <p className="mt-2 text-xs text-app-text-muted" data-testid="reading-practice-progress">
                        {attempted} / {totalPassages} perenggan dilatih
                      </p>
                      {average != null ? (
                        <p className="mt-1 text-xs font-semibold text-app-text">
                          Purata skor: {average} / 100
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-app-text-muted" data-testid="malay-formative-empty-evidence">
                          Mulakan latihan bacaan perenggan.
                        </p>
                      )}
                      <div
                        className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700"
                        aria-hidden
                      >
                        <div
                          className="h-full bg-app-primary transition-[width] motion-reduce:transition-none"
                          style={{ width: `${Math.round(progressRatio * 100)}%` }}
                        />
                      </div>
                      <Link
                        to={continueTo}
                        className="mt-4 inline-flex items-center justify-center rounded-full bg-app-primary px-4 py-2 text-xs font-bold text-white hover:brightness-110"
                        data-testid="reading-practice-cta"
                      >
                        {ctaLabel}
                      </Link>
                    </article>
                  );
                }

                if (card.activity_key === 'malay_unit_5') {
                  const u5 = card.unit5_progress;
                  const completed = u5?.completion?.completed_lessons ?? Number(card.progress?.completed_units || 0);
                  const total = u5?.completion?.total_lessons ?? Number(card.progress?.total_units || 9);
                  const teachingPct =
                    u5?.completion?.unit_teaching_progress_percent ??
                    u5?.completion?.completion_percent ??
                    Math.round((completed / Math.max(total, 1)) * 100);
                  const assessmentsCompleted =
                    u5?.completion?.assessments_completed ?? 0;
                  const assessmentUnitPct = u5?.completion?.assessment_completion_percent ?? 0;
                  const masteryMs = u5?.mastery_summary?.display_ms;
                  const continueLesson =
                    u5?.continue_lesson ??
                    u5?.lessons?.find((l) => l.topic_id === u5.continue_topic_id);
                  const continueTopicPct = continueLesson?.teaching_progress_percent ?? 0;
                  const continueAssessmentStatus = continueLesson?.assessment_status;
                  const continueAssessmentPct = continueLesson?.assessment_progress_percent ?? 0;
                  const continueAssessmentScore = continueLesson?.assessment_score;
                  const assessmentInProgress = continueAssessmentStatus === 'in_progress';
                  const assessmentCompleted = continueAssessmentStatus === 'completed';
                  const ctaKind = resolveStandardCardCtaKey(card);
                  const continueTo =
                    ctaKind === 'completed'
                      ? ROUTES.STUDENT_MALAY_LEARNING_PROGRESS
                      : continueRouteForActivity(card.activity_key);
                  const ctaLabel = assessmentInProgress
                    ? 'Continue Assessment'
                    : ctaKind === 'completed'
                      ? t('dashboard.malayFormative.viewDetail')
                      : ctaKind === 'start'
                        ? t('dashboard.malayFormative.start')
                        : t('dashboard.malayFormative.continueLearning', {
                            defaultValue: 'Continue Learning',
                          });
                  return (
                    <article
                      key={card.activity_key}
                      className="flex flex-col rounded-2xl border border-app-border bg-white/80 p-4 dark:bg-gray-900/40 md:col-span-2 lg:col-span-1"
                      data-testid={`malay-formative-card-${card.activity_key}`}
                      data-has-evidence={cardHasFormativeEvidence(card) ? 'true' : 'false'}
                    >
                      <h3 className="text-sm font-bold text-app-text">
                        {resolveActivityDisplayTitle(card, i18n.language, t)}
                      </h3>
                      <p className="mt-1 text-xs font-semibold text-app-primary">
                        {t(`dashboard.malayFormative.status.${resolveCardStatusKey(card)}`)}
                      </p>
                      <p className="mt-2 text-xs text-app-text-muted" data-testid="unit5-home-teaching-progress">
                        {t('dashboard.malayFormative.teachingProgress', {
                          defaultValue: 'Teaching Progress',
                        })}
                        : {teachingPct}%
                      </p>
                      <p className="mt-1 text-xs text-app-text-muted" data-testid="unit5-home-completion">
                        {t('dashboard.malayFormative.lessonsCompletedLabel', {
                          defaultValue: 'Lessons completed',
                        })}
                        : {completed} / {total}
                      </p>
                      <p className="mt-1 text-xs text-app-text-muted" data-testid="unit5-home-assessment-summary">
                        Assessments completed: {assessmentsCompleted} / {total} · {assessmentUnitPct}%
                      </p>
                      <div
                        className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700"
                        role="progressbar"
                        aria-valuenow={teachingPct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          className="h-full bg-app-primary transition-[width] motion-reduce:transition-none"
                          style={{ width: `${teachingPct}%` }}
                        />
                      </div>
                      {masteryMs ? (
                        <p className="mt-2 text-xs" data-testid="unit5-home-mastery">
                          <span className="font-semibold">
                            {t('dashboard.malayFormative.learningLevel', { defaultValue: 'Tahap pembelajaran' })}:
                          </span>{' '}
                          {masteryMs}
                        </p>
                      ) : (
                        <p
                          className="mt-2 text-xs text-app-text-muted"
                          data-testid="malay-formative-empty-evidence"
                        >
                          {t('dashboard.malayFormative.levels.notEnoughEvidence')}
                        </p>
                      )}
                      {continueLesson?.title_ms ? (
                        <div className="mt-2 text-xs text-app-text" data-testid="unit5-home-continue">
                          <p>
                            <span className="font-semibold">
                              {t('dashboard.malayFormative.currentLesson', {
                                defaultValue: 'Current lesson',
                              })}
                              :
                            </span>{' '}
                            {continueLesson.title_ms}
                          </p>
                          <p className="mt-0.5 text-app-text-muted">
                            Teaching: {continueTopicPct}%
                            {assessmentInProgress
                              ? ` · Assessment: ${continueAssessmentPct}%`
                              : assessmentCompleted && continueAssessmentScore != null
                                ? ` · Assessment Score: ${continueAssessmentScore}/100`
                                : ''}
                          </p>
                        </div>
                      ) : null}
                      {card.recommended_next_step?.message ? (
                        <p className="mt-2 text-xs text-app-text" data-testid="malay-formative-next">
                          {card.recommended_next_step.message}
                        </p>
                      ) : null}
                      <Link
                        to={continueTo}
                        className="mt-4 inline-flex items-center justify-center rounded-full bg-app-primary px-4 py-2 text-xs font-bold text-white hover:brightness-110"
                      >
                        {ctaLabel}
                      </Link>
                    </article>
                  );
                }

                const hasEvidence = cardHasFormativeEvidence(card);
                const statusKey = resolveCardStatusKey(card);
                const completed = Number(card.progress?.completed_units || 0);
                const total = Number(card.progress?.total_units || 0);
                const ctaKind = resolveStandardCardCtaKey(card);
                const continueTo =
                  ctaKind === 'completed'
                    ? ROUTES.STUDENT_MALAY_LEARNING_PROGRESS
                    : continueRouteForActivity(card.activity_key);
                const lastActivity = formatLastActivity(
                  card.progress?.last_activity_at,
                  t,
                );
                const ctaLabel =
                  ctaKind === 'completed'
                    ? t('dashboard.malayFormative.viewDetail')
                    : ctaKind === 'start'
                      ? t('dashboard.malayFormative.start')
                      : t('dashboard.malayFormative.resume');
                return (
                  <article
                    key={card.activity_key}
                    className="flex flex-col rounded-2xl border border-app-border bg-white/80 p-4 dark:bg-gray-900/40"
                    data-testid={`malay-formative-card-${card.activity_key}`}
                    data-has-evidence={hasEvidence ? 'true' : 'false'}
                  >
                    <h3 className="text-sm font-bold text-app-text">
                      {resolveActivityDisplayTitle(card, i18n.language, t)}
                    </h3>
                    <p className="mt-1 text-xs font-semibold text-app-primary">
                      {t(`dashboard.malayFormative.status.${statusKey}`)}
                    </p>
                    {hasEvidence && total > 0 ? (
                      <p className="mt-2 text-xs text-app-text-muted">
                        {t('dashboard.malayFormative.completionOf', {
                          completed,
                          total,
                        })}
                      </p>
                    ) : null}
                    {lastActivity ? (
                      <p className="mt-1 text-xs text-app-text-muted">{lastActivity}</p>
                    ) : null}
                    {hasEvidence && card.strongest_skill ? (
                      <p className="mt-2 text-xs" data-testid="malay-formative-strength">
                        <span className="font-semibold">
                          {t('dashboard.malayFormative.strength')}:
                        </span>{' '}
                        {masteryLabel(card.strongest_skill.current_level, t)}
                        {card.strongest_skill.skill_key
                          ? ` — ${card.strongest_skill.skill_key}`
                          : ''}
                      </p>
                    ) : (
                      <div className="mt-2 space-y-1" data-testid="malay-formative-empty-evidence">
                        <p className="text-xs font-medium text-app-text">
                          {t('dashboard.malayFormative.empty')}
                        </p>
                        <p className="text-xs text-app-text-muted">
                          {t('dashboard.malayFormative.emptyCardHint')}
                        </p>
                      </div>
                    )}
                    {hasEvidence && card.recommended_next_step?.message ? (
                      <p className="mt-2 text-xs text-app-text" data-testid="malay-formative-next">
                        <span className="font-semibold">
                          {t('dashboard.malayFormative.focusNext')}:
                        </span>{' '}
                        {card.recommended_next_step.message}
                      </p>
                    ) : null}
                    <Link
                      to={continueTo}
                      className="mt-4 inline-flex items-center justify-center rounded-full bg-app-primary px-4 py-2 text-xs font-bold text-white hover:brightness-110"
                    >
                      {ctaLabel}
                    </Link>
                  </article>
                );
              })}
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
